import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck, LoaderCircle, Reply, Send, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { getSocket } from '../../lib/socket';
import type { AuthenticatedUser, NotificationItem } from '../../types';
import { getNotifications, markNotificationsRead, sendNotification } from './shellApi';

export function NotificationsPopover({ user }: { user: AuthenticatedUser }) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [compose, setCompose] = useState('');
  const [replyingTo, setReplyingTo] = useState<NotificationItem | null>(null);
  const [reply, setReply] = useState('');
  const [feedback, setFeedback] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const notifications = useQuery({
    queryKey: queryKeys.notifications,
    queryFn: getNotifications,
    refetchInterval: 45_000,
    retry: false,
  });
  const markRead = useMutation({ mutationFn: markNotificationsRead });
  const send = useMutation({ mutationFn: ({ body, target }: { body: string; target?: number }) => sendNotification(body, target) });

  useEffect(() => {
    const socket = getSocket();
    const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    socket.on('notification', refresh);
    socket.on('connect', refresh);
    return () => {
      socket.off('notification', refresh);
      socket.off('connect', refresh);
    };
  }, [queryClient]);

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', closeOutside);
    return () => document.removeEventListener('mousedown', closeOutside);
  }, [open]);

  async function markAllRead() {
    try {
      await markRead.mutateAsync();
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    } catch (error) {
      setFeedback(errorMessage(error, 'Could not mark messages as read.'));
    }
  }

  async function sendMessage(body: string, target?: number) {
    const trimmed = body.trim();
    if (!trimmed) return;
    setFeedback('');
    try {
      await send.mutateAsync(target === undefined ? { body: trimmed } : { body: trimmed, target });
      setCompose('');
      setReply('');
      setReplyingTo(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
    } catch (error) {
      setFeedback(errorMessage(error, 'Could not send your message.'));
    }
  }

  const unread = notifications.data?.unread ?? 0;
  const isAdmin = user.caps.admin;

  return (
    <div className="notification-root" ref={rootRef}>
      <button className="icon-button topbar-icon" type="button" aria-label="Messages" title="Messages" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
        <Bell size={18} />
        {unread > 0 ? <span className="notification-count">{unread > 99 ? '99+' : unread}</span> : null}
      </button>
      {open ? (
        <section className="notification-panel" aria-label="Messages">
          <header>
            <strong>Messages</strong>
            <div>
              <button className="icon-button" type="button" title="Mark all read" aria-label="Mark all messages read" disabled={markRead.isPending} onClick={markAllRead}><CheckCheck size={17} /></button>
              <button className="icon-button" type="button" title="Close" aria-label="Close messages" onClick={() => setOpen(false)}><X size={17} /></button>
            </div>
          </header>

          <div className="notification-list">
            {notifications.isPending ? <div className="notification-empty"><LoaderCircle className="spin" size={17} />Loading messages</div> : null}
            {notifications.isError ? <div className="notification-error">{errorMessage(notifications.error, 'Could not load messages.')}</div> : null}
            {notifications.data?.items.length === 0 ? <div className="notification-empty">No messages yet.</div> : null}
            {notifications.data?.items.map((item) => {
              const target = item.from_user_id || item.subject_user_id || undefined;
              const canReply = Boolean(target) || !isAdmin;
              return (
                <article className={`notification-row ${item.read_at ? '' : 'unread'}`} key={item.id}>
                  <div className="notification-meta"><strong>{senderLabel(item)}</strong><time>{formatWhen(item.created_at)}</time></div>
                  <p>{item.body}</p>
                  {canReply ? <button className="reply-button" type="button" onClick={() => { setReplyingTo(item); setReply(''); }}>Reply <Reply size={13} /></button> : null}
                </article>
              );
            })}
          </div>

          {replyingTo ? (
            <div className="notification-compose">
              <label>Reply to {senderLabel(replyingTo)}</label>
              <textarea rows={2} value={reply} onChange={(event) => setReply(event.target.value)} autoFocus />
              <div className="compose-actions">
                <button className="text-button" type="button" onClick={() => setReplyingTo(null)}>Cancel</button>
                <button className="secondary-button" type="button" disabled={send.isPending || !reply.trim()} onClick={() => sendMessage(reply, replyingTo.from_user_id || replyingTo.subject_user_id || undefined)}><Send size={14} />Send</button>
              </div>
            </div>
          ) : !isAdmin ? (
            <div className="notification-compose">
              <label htmlFor="admin-message">Message the admin</label>
              <textarea id="admin-message" rows={2} value={compose} onChange={(event) => setCompose(event.target.value)} />
              <button className="secondary-button full-width" type="button" disabled={send.isPending || !compose.trim()} onClick={() => sendMessage(compose)}><Send size={14} />Send to admin</button>
            </div>
          ) : null}
          {feedback ? <div className="notification-error" role="alert">{feedback}</div> : null}
        </section>
      ) : null}
    </div>
  );
}

function senderLabel(notification: NotificationItem): string {
  if (notification.kind === 'usage_alert') return 'Usage alert';
  if (!notification.from_user_id) return 'System';
  return [notification.from_first, notification.from_last].filter(Boolean).join(' ') || notification.from_name || 'Someone';
}

function formatWhen(milliseconds: number): string {
  if (!milliseconds) return '';
  return new Date(milliseconds).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
